// src/components/Orders/Index.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "material-design-icons/iconfont/material-icons.css";
import { ReactSortable } from "react-sortablejs";
import { useOrders } from "./useOrders";
import { useDarkMode } from "../../hooks/useDarkMode";
import buildPrintTicket from "./PrintTicket";
import TicketCard from "./TicketCard";
import TopBar from "./TopBar";
import FooterBar from "./FooterBar";
import SettingsModal from "../Settings";
import { apiFetch, API_BASE } from "../../utils/api";
import socket from "../../socket";
const PARK_MODE_MANUAL = "manual";
const PARK_MODE_SPECIFIC = "specific_time";
const PARK_MODE_DURATION = "after_duration";
const PRINT_SERVER_URL = "https://printserver.myods.club/api/api/jobs/escpos";
function getTimeZoneParts(date, timeZone) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false
  }).formatToParts(date);
  const read = type => Number(parts.find(part => part.type === type)?.value ?? 0);
  return {
    year: read("year"),
    month: read("month"),
    day: read("day"),
    hour: read("hour"),
    minute: read("minute"),
    second: read("second")
  };
}
function zonedDateTimeToUtc(parts, timeZone) {
  const baseUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour ?? 0, parts.minute ?? 0, parts.second ?? 0, 0);
  const offsetParts = getTimeZoneParts(new Date(baseUtc), timeZone);
  const offsetUtc = Date.UTC(offsetParts.year, offsetParts.month - 1, offsetParts.day, offsetParts.hour, offsetParts.minute, offsetParts.second, 0);
  return new Date(baseUtc - (offsetUtc - baseUtc));
}
function addDaysToLocalDate(parts, daysToAdd) {
  const shifted = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + daysToAdd));
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate()
  };
}
function buildScheduledUnparkIso({
  parkMode,
  stationTimezone,
  specificTime,
  durationHours,
  durationMinutes
}) {
  if (parkMode === PARK_MODE_MANUAL) return null;
  if (parkMode === PARK_MODE_SPECIFIC) {
    if (!specificTime || !specificTime.includes(":")) return null;
    const [hourText, minuteText] = specificTime.split(":");
    const hour = Number(hourText);
    const minute = Number(minuteText);
    if (!Number.isInteger(hour) || !Number.isInteger(minute)) return null;
    const now = new Date();
    const nowLocal = getTimeZoneParts(now, stationTimezone);
    let localDate = {
      year: nowLocal.year,
      month: nowLocal.month,
      day: nowLocal.day
    };
    let candidate = zonedDateTimeToUtc({
      ...localDate,
      hour,
      minute,
      second: 0
    }, stationTimezone);
    if (candidate.getTime() <= now.getTime()) {
      localDate = addDaysToLocalDate(localDate, 1);
      candidate = zonedDateTimeToUtc({
        ...localDate,
        hour,
        minute,
        second: 0
      }, stationTimezone);
    }
    return candidate.toISOString();
  }
  if (parkMode === PARK_MODE_DURATION) {
    const hours = Math.max(0, Number(durationHours) || 0);
    const minutes = Math.max(0, Number(durationMinutes) || 0);
    const totalMinutes = hours * 60 + minutes;
    if (totalMinutes <= 0) return null;
    return new Date(Date.now() + totalMinutes * 60 * 1000).toISOString();
  }
  return null;
}
function formatScheduledPreview(unparkIso, stationTimezone) {
  if (!unparkIso) return "Manual unpark";
  const parsed = new Date(unparkIso);
  if (Number.isNaN(parsed.getTime())) return "Invalid schedule";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: stationTimezone,
    weekday: "short",
    hour: "numeric",
    minute: "2-digit",
    month: "short",
    day: "numeric"
  }).format(parsed);
}
function formatTimeInputInZone(date, timeZone) {
  const parts = getTimeZoneParts(date, timeZone);
  return `${String(parts.hour).padStart(2, "0")}:${String(parts.minute).padStart(2, "0")}`;
}
export default function Index({
  stationId,
  role,
  stationName,
  stationTimezone = "UTC"
}) {
  const ticketFirePromotionQueueRef = useRef([]);
  const {
    orders,
    setOrders,
    fetchOrders,
    fireCourse,
    FireAllItems,
    emitFireTicket,
    emitBumpTicket,
    emitBumpCourse,
    emitBumpItem,
    emitBumpAllCoursesForTicket,
    emitUnbumpTicket,
    parkTicket,
    unparkTicket,
    fireItem,
    lastUnparkSignal,
    emitTicketLocationSync,
    emitTicketPageSync,
    screenMountId,
    settings
  } = useOrders({
    stationId,
    role,
    stationTimezone,
    onVoidedItem: (payload = {}) => {
      const targetStationId = Number(payload?.station_id);
      if (Number.isFinite(targetStationId) && Number(targetStationId) !== Number(stationId)) {
        return;
      }
      playVoidedItemSoundOnce(payload);
    },
    onCourseFired: (payload = {}) => {
      const targetStationId = Number(payload?.station_id);
      if (Number.isFinite(targetStationId) && Number(targetStationId) !== Number(stationId)) {
        return;
      }
      playCourseFiredSoundOnce(payload);
    },
    onTicketFirePromotionRequested: (payload = {}) => {
      ticketFirePromotionQueueRef.current.push(payload);
    }
  });
  const {
    isDark,
    toggleDarkMode
  } = useDarkMode("kdsDarkMode");
  const [showSettings, setShowSettings] = useState(false);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const [isPeekMode, setIsPeekMode] = useState(false);
  const [peekedOrder, setPeekedOrder] = useState(null);
  const [peekModeIntent, setPeekModeIntent] = useState(null);
  const [isFireCourseMode, setIsFireCourseMode] = useState(false);
  const [isFireTicketMode, setIsFireTicketMode] = useState(false);
  const [isFireItemMode, setIsFireItemMode] = useState(false);
  const [isPrintCourseMode, setIsPrintCourseMode] = useState(false);
  const [isPrintTicketMode, setIsPrintTicketMode] = useState(false);
  const [isParkTicketMode, setIsParkTicketMode] = useState(false);
  const [isUnparkModalOpen, setIsUnparkModalOpen] = useState(false);
  const [selectedParkedTicketId, setSelectedParkedTicketId] = useState(null);
  const [isParkOptionsModalOpen, setIsParkOptionsModalOpen] = useState(false);
  const [parkSelectionTicket, setParkSelectionTicket] = useState(null);
  const [parkMode, setParkMode] = useState(PARK_MODE_MANUAL);
  const [specificUnparkTime, setSpecificUnparkTime] = useState("");
  const [durationHours, setDurationHours] = useState("0");
  const [durationMinutes, setDurationMinutes] = useState("15");
  const durationPresetMinutes = [15, 30, 45, 60, 90, 120];
  const specificTimePresetMinutes = [15, 30, 60];
  const [isDragging, setIsDragging] = useState(false);
  const skipNextGridSetList = useRef(false);
  const currentPageRef = useRef(0);
  const TICKETS_PER_PAGE = 6;
  const [currentPage, setCurrentPage] = useState(0);
  const keyForOrder = o => `${o.ticket_instance_id}::${o.card ?? 0}`;
  const chunkIntoPages = (keys, size) => {
    const out = [];
    for (let i = 0; i < keys.length; i += size) out.push(keys.slice(i, i + size));
    return out.length ? out : [[]];
  };
  const [pages, setPages] = useState([[]]);
  const previousParkStateRef = useRef(new Map());
  const handledUnparkPromotionNonceRef = useRef(null);
  const orderByKey = useMemo(() => {
    const map = Object.create(null);
    for (const o of orders) map[keyForOrder(o)] = o;
    return map;
  }, [orders]);
  useEffect(() => {
    setPages(prev => {
      const known = new Set(prev.flat());
      const visibleOrders = orders.filter(o => !o.isParked);
      const incomingKeys = visibleOrders.map(o => keyForOrder(o));
      const justUnparkedKeys = visibleOrders.map(o => {
        const key = keyForOrder(o);
        return previousParkStateRef.current.get(key) === true ? key : null;
      }).filter(Boolean);
      const justUnparkedSet = new Set(justUnparkedKeys);
      const stillThere = prev.map(pg => pg.filter(k => incomingKeys.includes(k)));
      const newOnes = incomingKeys.filter(k => !known.has(k));
      const retained = stillThere.flat().filter(k => !justUnparkedSet.has(k));
      const fresh = newOnes.filter(k => !justUnparkedSet.has(k));
      const flattened = justUnparkedKeys.concat(retained, fresh);
      const next = chunkIntoPages(flattened, TICKETS_PER_PAGE);
      if (justUnparkedKeys.length > 0) {
        setCurrentPage(0);
      }
      if (currentPage >= next.length) {
        setCurrentPage(Math.max(0, next.length - 1));
      }
      return next;
    });
    previousParkStateRef.current = new Map(orders.map(order => [keyForOrder(order), !!order?.isParked]));
  }, [orders, currentPage]);
  const pageKeys = pages[currentPage] ?? [];
  const pageCards = pageKeys.map((k, i) => ({
    key: `${k}-card-${i}`,
    order: orderByKey[k],
    orderIndex: i
  })).filter(card => !!card.order);
  const hasFullTicketPage = pageCards.length >= TICKETS_PER_PAGE;
  const touchScrollGutterPx = hasFullTicketPage ? 52 : 0;
  const [isPagePickerOpen, setIsPagePickerOpen] = useState(false);
  const [pendingDropKey, setPendingDropKey] = useState(null);
  const getStableTicketRef = useCallback(order => {
    const card = Number.isFinite(Number(order?.card)) ? Number(order.card) : 0;
    if (order?.order_id != null) {
      return `${String(order.order_id).trim()}::${card}`;
    }
    if (order?.ticket_instance_id) {
      return `${String(order.ticket_instance_id).split("-")[0].trim()}::${card}`;
    }
    return null;
  }, []);
  const findTicketKey = useCallback((payload = {}) => {
    const targetTicket = payload?.ticket_instance_id ? String(payload.ticket_instance_id).split("-")[0].trim() : null;
    const targetOrderId = payload?.order_id != null ? String(payload.order_id).trim() : null;
    const targetCard = Number.isFinite(Number(payload?.card)) ? Number(payload.card) : 0;
    const match = orders.find(order => {
      const orderTicket = order?.ticket_instance_id ? String(order.ticket_instance_id).split("-")[0].trim() : null;
      const orderOrderId = order?.order_id != null ? String(order.order_id).trim() : null;
      const orderCard = Number.isFinite(Number(order?.card)) ? Number(order.card) : 0;
      const sameCard = orderCard === targetCard;
      const sameTicket = targetTicket && orderTicket === targetTicket;
      const sameOrder = targetOrderId != null && orderOrderId === targetOrderId;
      return sameCard && (sameTicket || sameOrder);
    });
    return match ? keyForOrder(match) : null;
  }, [orders]);
  const buildSyncedPageOrder = useCallback((remoteOrderedRefs = []) => {
    const visibleOrders = orders.filter(order => !order?.isParked);
    const keyByRef = new Map();
    const visibleKeys = visibleOrders.map(order => {
      const key = keyForOrder(order);
      const stableRef = getStableTicketRef(order);
      if (stableRef) {
        keyByRef.set(stableRef, key);
      }
      return key;
    });
    const prioritized = (Array.isArray(remoteOrderedRefs) ? remoteOrderedRefs : []).map(value => String(value || "").trim()).map(stableRef => keyByRef.get(stableRef) || null).filter(Boolean);
    const prioritizedSet = new Set(prioritized);
    const remainder = visibleKeys.filter(key => !prioritizedSet.has(key));
    return chunkIntoPages(prioritized.concat(remainder), TICKETS_PER_PAGE);
  }, [getStableTicketRef, orders]);
  const shouldApplyRemoteTicketLocationEvent = useCallback((data = {}) => {
    const mirroredStationIds = Array.isArray(data?.mirrored_station_ids) ? data.mirrored_station_ids.map(value => Number(value)).filter(value => Number.isFinite(value)) : [];
    if (mirroredStationIds.length > 0) {
      return mirroredStationIds.includes(Number(stationId));
    }
    const method = String(settings?.ticket_location_sync_method || "none").trim().toLowerCase();
    if (method === "all_stations") return true;
    if (method === "expo" && String(data?.sender_role || "").trim().toLowerCase() === "expo") {
      return true;
    }
    return false;
  }, [settings?.ticket_location_sync_method, stationId]);
  const shouldApplyRemoteTicketPageEvent = useCallback((data = {}) => {
    const mirroredStationIds = Array.isArray(data?.mirrored_station_ids) ? data.mirrored_station_ids.map(value => Number(value)).filter(value => Number.isFinite(value)) : [];
    if (mirroredStationIds.length > 0) {
      return mirroredStationIds.includes(Number(stationId));
    }
    const method = String(settings?.ticket_page_sync_method || "none").trim().toLowerCase();
    if (method === "all_stations") return true;
    if (method === "expo" && String(data?.sender_role || "").trim().toLowerCase() === "expo") {
      return true;
    }
    return false;
  }, [settings?.ticket_page_sync_method, stationId]);
  const goToPage = useCallback((nextPage, {
    sync = false
  } = {}) => {
    const totalPageCount = Math.max(1, pages.length);
    const safeTarget = Math.max(0, Math.min(Number(nextPage) || 0, totalPageCount - 1));
    setCurrentPage(safeTarget);
    if (sync && safeTarget !== currentPageRef.current) {
      emitTicketPageSync({
        pageIndex: safeTarget
      });
    }
  }, [emitTicketPageSync, pages.length]);
  const moveKeyToGlobalIndex = (key, targetGlobalIndex) => {
    setPages(prev => {
      const keys = prev.flat();
      const fromIdx = keys.indexOf(key);
      if (fromIdx === -1) return prev;
      keys.splice(fromIdx, 1);
      const clamped = Math.max(0, Math.min(targetGlobalIndex, keys.length));
      keys.splice(clamped, 0, key);
      const next = chunkIntoPages(keys, TICKETS_PER_PAGE);
      if (currentPage >= next.length) setCurrentPage(Math.max(0, next.length - 1));
      return next;
    });
  };
  useEffect(() => {
    const queue = ticketFirePromotionQueueRef.current;
    if (!Array.isArray(queue) || queue.length === 0) return;
    while (queue.length > 0) {
      const pending = queue.shift();
      const key = findTicketKey(pending);
      if (!key) continue;
      moveKeyToGlobalIndex(key, 0);
    }
  }, [currentPage, findTicketKey, orders, pages, role, stationId]);
  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);
  useEffect(() => {
    const handleRemoteTicketLocationSync = (data = {}) => {
      if (data?.sender_screen_id === screenMountId) return;
      if (!shouldApplyRemoteTicketLocationEvent(data)) return;
      const remoteOrderedRefs = Array.isArray(data?.ordered_refs) ? data.ordered_refs : Array.isArray(data?.ordered_keys) ? data.ordered_keys : [];
      if (!remoteOrderedRefs.length) return;
      const nextPages = buildSyncedPageOrder(remoteOrderedRefs);
      const targetGlobalIndex = Number(data?.target_global_index);
      const nextPageIndex = Number.isFinite(targetGlobalIndex) ? Math.max(0, Math.floor(targetGlobalIndex / TICKETS_PER_PAGE)) : currentPage;
      setPages(nextPages);
      setCurrentPage(Math.min(nextPageIndex, Math.max(0, nextPages.length - 1)));
    };
    socket.on("ticket-location-sync", handleRemoteTicketLocationSync);
    return () => {
      socket.off("ticket-location-sync", handleRemoteTicketLocationSync);
    };
  }, [buildSyncedPageOrder, currentPage, screenMountId, shouldApplyRemoteTicketLocationEvent]);
  useEffect(() => {
    const handleRemoteTicketPageSync = (data = {}) => {
      if (data?.sender_screen_id === screenMountId) return;
      if (!shouldApplyRemoteTicketPageEvent(data)) return;
      const requestedPage = Number(data?.page_index);
      if (!Number.isFinite(requestedPage)) return;
      const totalPageCount = Math.max(1, pages.length);
      setCurrentPage(Math.max(0, Math.min(requestedPage, totalPageCount - 1)));
    };
    socket.on("ticket-page-sync", handleRemoteTicketPageSync);
    return () => {
      socket.off("ticket-page-sync", handleRemoteTicketPageSync);
    };
  }, [pages.length, screenMountId, shouldApplyRemoteTicketPageEvent]);
  useEffect(() => {
    if (!lastUnparkSignal?.nonce) return;
    if (handledUnparkPromotionNonceRef.current === lastUnparkSignal.nonce) return;
    const normalizedTicket = lastUnparkSignal.ticket_instance_id ? String(lastUnparkSignal.ticket_instance_id).split("-")[0] : null;
    const matchingOrder = orders.find(order => {
      if (order?.isParked) return false;
      const sameTicket = normalizedTicket && String(order?.ticket_instance_id || "").split("-")[0] === normalizedTicket;
      const sameOrder = lastUnparkSignal.order_id != null && String(order?.order_id ?? "") === String(lastUnparkSignal.order_id);
      return sameTicket || sameOrder;
    });
    if (!matchingOrder) return;
    const promotionKey = keyForOrder(matchingOrder);
    handledUnparkPromotionNonceRef.current = lastUnparkSignal.nonce;
    moveKeyToGlobalIndex(promotionKey, 0);
    setCurrentPage(0);
  }, [lastUnparkSignal, orders]);
  const handlePickPage = targetPageIndex => {
    if (!pendingDropKey) return;
    const targetGlobalIndex = targetPageIndex * TICKETS_PER_PAGE + (TICKETS_PER_PAGE - 1);
    const currentVisibleKeys = pages.flat();
    const fromIndex = currentVisibleKeys.indexOf(pendingDropKey);
    const nextVisibleKeys = [...currentVisibleKeys];
    if (fromIndex !== -1) {
      nextVisibleKeys.splice(fromIndex, 1);
    }
    const clampedIndex = Math.max(0, Math.min(targetGlobalIndex, nextVisibleKeys.length));
    nextVisibleKeys.splice(clampedIndex, 0, pendingDropKey);
    moveKeyToGlobalIndex(pendingDropKey, targetGlobalIndex);
    emitTicketLocationSync({
      order: orderByKey[pendingDropKey] || null,
      ticketKey: pendingDropKey,
      orderedKeys: nextVisibleKeys,
      targetGlobalIndex: clampedIndex
    });
    setIsPagePickerOpen(false);
    setPendingDropKey(null);
  };
  const [soundSettings, setSoundSettings] = useState({
    newTicket: {
      sound: "",
      durationMs: 1500
    },
    addonReceived: {
      sound: "",
      durationMs: 1500
    },
    voidedItem: {
      sound: "",
      durationMs: 1500
    },
    courseFired: {
      sound: "",
      durationMs: 1500
    },
    courseBumped: {
      sound: "",
      durationMs: 1500
    },
    ticketBumped: {
      sound: "",
      durationMs: 1500
    }
  });
  const [soundOptions, setSoundOptions] = useState([]);
  const [availablePrinters, setAvailablePrinters] = useState([]);
  const [isAudioUnlocked, setIsAudioUnlocked] = useState(false);
  const [soundSettingsLoaded, setSoundSettingsLoaded] = useState(false);
  const audioRef = useRef(null);
  const timeoutRef = useRef(null);
  const playedNewTicketSoundKeysRef = useRef(new Map());
  const playedVoidedItemSoundKeysRef = useRef(new Map());
  const playedCourseFiredSoundKeysRef = useRef(new Map());
  const audioUnlockedRef = useRef(false);
  const pendingSoundEventRef = useRef(null);
  const soundSettingsRef = useRef(soundSettings);
  const soundSettingsLoadedRef = useRef(soundSettingsLoaded);
  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };
  const buildSoundSrc = sound => {
    const opt = soundOptions.find(o => o.file === sound);
    const base = API_BASE.replace(/\/$/, "");
    const pathPiece = opt?.type === "system" ? "/sounds" : "/uploads/sounds";
    return `${base}${pathPiece}/${encodeURIComponent(sound)}`;
  };
  const playEventSound = (eventKey, options = {}) => {
    const {
      fromUnlock = false
    } = options;
    const currentSoundSettings = soundSettingsRef.current || {};
    const cfg = currentSoundSettings[eventKey];
    if (!soundSettingsLoadedRef.current) {
      pendingSoundEventRef.current = eventKey;
      return;
    }
    if (!cfg || !cfg.sound) {
      return;
    }
    if (!audioUnlockedRef.current && !fromUnlock) {
      pendingSoundEventRef.current = eventKey;
      return;
    }
    const {
      sound,
      durationMs
    } = cfg;
    try {
      stopAudio();
      const src = buildSoundSrc(sound);
      const audio = new Audio(src);
      audioRef.current = audio;
      if (durationMs && durationMs > 0) {
        audio.loop = true;
        timeoutRef.current = window.setTimeout(() => {
          if (audioRef.current === audio) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            audioRef.current = null;
          }
          timeoutRef.current = null;
        }, durationMs);
        audio.onended = null;
      } else {
        audio.loop = false;
        audio.onended = () => {
          if (audioRef.current === audio) audioRef.current = null;
        };
      }
      audio.play().catch(err => {
        const blockedByAutoplay = err?.name === "NotAllowedError" || /user gesture|interact|allowed/i.test(String(err?.message || ""));
        if (blockedByAutoplay) {
          pendingSoundEventRef.current = eventKey;
          audioUnlockedRef.current = false;
          setIsAudioUnlocked(false);
        }
        console.error(`Failed to play "${eventKey}" sound:`, err);
      });
    } catch (err) {
      console.error(`Error setting up "${eventKey}" sound:`, err);
    }
  };
  useEffect(() => {
    soundSettingsRef.current = soundSettings;
  }, [soundSettings]);
  useEffect(() => {
    soundSettingsLoadedRef.current = soundSettingsLoaded;
  }, [soundSettingsLoaded]);
  useEffect(() => () => stopAudio(), []);
  useEffect(() => {
    const unlockAudio = event => {
      if (audioUnlockedRef.current) return;
      audioUnlockedRef.current = true;
      setIsAudioUnlocked(true);
      const pendingEvent = pendingSoundEventRef.current;
      pendingSoundEventRef.current = null;
      if (pendingEvent) {
        window.setTimeout(() => {
          playEventSound(pendingEvent, {
            fromUnlock: true
          });
        }, 0);
      }
    };
    document.addEventListener("pointerdown", unlockAudio, {
      passive: true
    });
    document.addEventListener("keydown", unlockAudio, {
      passive: true
    });
    return () => {
      document.removeEventListener("pointerdown", unlockAudio);
      document.removeEventListener("keydown", unlockAudio);
    };
  }, [soundOptions, soundSettings, soundSettingsLoaded]);
  const normalizeSoundTicketKey = (payload = {}) => {
    const ticketKey = String(payload?.ticket_instance_id || "").trim();
    if (ticketKey) return `ticket:${ticketKey}`;
    const orderKey = payload?.order_id ?? payload?.ID ?? null;
    return orderKey != null ? `order:${String(orderKey).trim()}` : null;
  };
  const normalizeVoidedItemSoundKey = (payload = {}) => {
    const ticketKey = String(payload?.ticket_instance_id || "").trim();
    const itemKey = String(payload?.item_id || payload?.ID || "").trim();
    if (ticketKey && itemKey) return `ticket:${ticketKey}:item:${itemKey}`;
    const orderKey = payload?.order_id ?? payload?.ID ?? null;
    if (orderKey != null && itemKey) return `order:${String(orderKey).trim()}:item:${itemKey}`;
    return itemKey ? `item:${itemKey}` : null;
  };
  const playNewTicketSoundOnce = (payload = {}) => {
    const key = normalizeSoundTicketKey(payload);
    if (!key) {
      playEventSound("newTicket");
      return;
    }
    const now = Date.now();
    const seenAt = Number(playedNewTicketSoundKeysRef.current.get(key) || 0);
    if (now - seenAt < 5000) {
      return;
    }
    playedNewTicketSoundKeysRef.current.set(key, now);
    if (playedNewTicketSoundKeysRef.current.size > 100) {
      const cutoff = now - 60000;
      for (const [entryKey, entryTime] of playedNewTicketSoundKeysRef.current.entries()) {
        if (Number(entryTime) < cutoff) {
          playedNewTicketSoundKeysRef.current.delete(entryKey);
        }
      }
    }
    playEventSound("newTicket");
  };
  const playVoidedItemSoundOnce = (payload = {}) => {
    const key = normalizeVoidedItemSoundKey(payload);
    if (!key) {
      playEventSound("voidedItem");
      return;
    }
    const now = Date.now();
    const seenAt = Number(playedVoidedItemSoundKeysRef.current.get(key) || 0);
    if (now - seenAt < 1500) {
      return;
    }
    playedVoidedItemSoundKeysRef.current.set(key, now);
    if (playedVoidedItemSoundKeysRef.current.size > 100) {
      const cutoff = now - 60000;
      for (const [entryKey, entryTime] of playedVoidedItemSoundKeysRef.current.entries()) {
        if (Number(entryTime) < cutoff) {
          playedVoidedItemSoundKeysRef.current.delete(entryKey);
        }
      }
    }
    playEventSound("voidedItem");
  };
  const playCourseFiredSoundOnce = (payload = {}) => {
    const ticketKey = String(payload?.ticket_instance_id || "").trim();
    const orderKey = payload?.order_id != null ? String(payload.order_id).trim() : "";
    const courseKey = Number.isFinite(Number(payload?.course)) ? String(Number(payload.course)) : "unknown";
    const key = ticketKey ? `ticket:${ticketKey}:course:${courseKey}` : orderKey ? `order:${orderKey}:course:${courseKey}` : null;
    if (!key) {
      playEventSound("courseFired");
      return;
    }
    const now = Date.now();
    const seenAt = Number(playedCourseFiredSoundKeysRef.current.get(key) || 0);
    if (now - seenAt < 1500) {
      return;
    }
    playedCourseFiredSoundKeysRef.current.set(key, now);
    if (playedCourseFiredSoundKeysRef.current.size > 100) {
      const cutoff = now - 60000;
      for (const [entryKey, entryTime] of playedCourseFiredSoundKeysRef.current.entries()) {
        if (Number(entryTime) < cutoff) {
          playedCourseFiredSoundKeysRef.current.delete(entryKey);
        }
      }
    }
    playEventSound("courseFired");
  };
  const normalizeTicketRoot = value => String(value || "").split("-")[0].trim();
  const normalizeItemRoot = value => String(value || "").split("-")[0].trim();
  const isAddonFlag = value => value === true || value === 1 || value === "1" || String(value || "").toLowerCase() === "true";
  const isUtilityKitchenItem = (item = {}) => {
    const name = String(item?.Name || "").trim();
    const category = String(item?.Category || "").trim();
    const isSeparator = /^\*{3,}.*\*{3,}$/.test(name) || category === "Course Separator";
    const isMessage = name === "* MESSAGE *";
    const isFireCategoryControl = !!String(category).match(/^FIRE\b/i);
    const isFireLine = name === "------ FIRE ------" || /FIRE/i.test(name) && name !== "* MESSAGE *" || typeof item?.ID === "string" && item.ID.includes("FIRE");
    return isSeparator || isMessage || isFireLine || isFireCategoryControl;
  };
  const classifyIncomingTicketSound = useCallback((payload = {}) => {
    const payloadItems = Array.isArray(payload?.Item) ? payload.Item : payload?.Item ? [payload.Item] : [];
    const existingOrder = orders.find(order => {
      const sameOrder = payload?.order_id != null && String(order?.order_id ?? "") === String(payload.order_id);
      const sameLegacyOrder = payload?.ID != null && String(order?.order_id ?? "") === String(payload.ID);
      const sameTicket = payload?.ticket_instance_id && normalizeTicketRoot(order?.ticket_instance_id) === normalizeTicketRoot(payload.ticket_instance_id);
      return sameOrder || sameLegacyOrder || sameTicket;
    });
    if (!existingOrder) {
      return {
        soundKey: "newTicket",
        reason: "brand-new-ticket"
      };
    }
    const existingRealItemIds = new Set((existingOrder?.items || []).filter(item => !isUtilityKitchenItem(item)).map(item => normalizeItemRoot(item?.ID)).filter(Boolean));
    const incomingRealItems = payloadItems.filter(item => !isUtilityKitchenItem(item));
    const introducedItems = incomingRealItems.filter(item => {
      const itemId = normalizeItemRoot(item?.ID);
      return itemId && !existingRealItemIds.has(itemId);
    });
    if (introducedItems.length > 0 && introducedItems.every(item => isAddonFlag(item?.is_addon) || isAddonFlag(item?.isAddon))) {
      return {
        soundKey: "addonReceived",
        reason: "existing-ticket-addon-items",
        introducedItemIds: introducedItems.map(item => item?.ID).filter(Boolean)
      };
    }
    return {
      soundKey: null,
      reason: introducedItems.length > 0 ? "existing-ticket-non-addon-update" : "existing-ticket-no-new-items",
      introducedItemIds: introducedItems.map(item => item?.ID).filter(Boolean)
    };
  }, [orders]);
  useEffect(() => {
    if (!stationId) return;
    let cancelled = false;
    if (!cancelled) setSoundSettingsLoaded(false);
    async function fetchSoundSettings() {
      try {
        const settingsRes = await apiFetch(`/settings/sounds/${stationId}`);
        const data = await settingsRes.json();
        const nextSettings = {
          newTicket: {
            sound: data.new_ticket_sound ?? data.newTicketSound ?? data.newTicket?.sound ?? "",
            durationMs: data.new_ticket_duration_ms ?? data.newTicketDurationMs ?? data.newTicket?.durationMs ?? 1500
          },
          addonReceived: {
            sound: data.addon_received_sound ?? data.addonReceivedSound ?? data.addonReceived?.sound ?? "",
            durationMs: data.addon_received_duration_ms ?? data.addonReceivedDurationMs ?? data.addonReceived?.durationMs ?? 1500
          },
          voidedItem: {
            sound: data.voided_item_sound ?? data.voidedItemSound ?? data.voidedItem?.sound ?? "",
            durationMs: data.voided_item_duration_ms ?? data.voidedItemDurationMs ?? data.voidedItem?.durationMs ?? 1500
          },
          courseFired: {
            sound: data.course_fired_sound ?? data.courseFiredSound ?? data.courseFired?.sound ?? "",
            durationMs: data.course_fired_duration_ms ?? data.courseFiredDurationMs ?? data.courseFired?.durationMs ?? 1500
          },
          ticketBumped: {
            sound: data.ticket_bumped_sound ?? data.ticketBumpedSound ?? data.ticketBumped?.sound ?? "",
            durationMs: data.ticket_bumped_duration_ms ?? data.ticketBumpedDurationMs ?? data.ticketBumped?.durationMs ?? 1500
          },
          courseBumped: {
            sound: data.course_bumped_sound ?? data.courseBumpedSound ?? data.courseBumped?.sound ?? "",
            durationMs: data.course_bumped_duration_ms ?? data.courseBumpedDurationMs ?? data.courseBumped?.durationMs ?? 1500
          }
        };
        if (!cancelled) {
          setSoundSettings(nextSettings);
        }
        const optionsRes = await apiFetch("/settings/sounds/options");
        const optionsData = await optionsRes.json();
        if (!cancelled) {
          setSoundOptions(optionsData || []);
        }
      } catch (err) {
        console.error("Failed to fetch station sound settings:", err);
      } finally {
        if (!cancelled) {
          setSoundSettingsLoaded(true);
        }
      }
    }
    fetchSoundSettings();
    return () => {
      cancelled = true;
    };
  }, [stationId]);
  useEffect(() => {
    if (!stationId) return;
    let cancelled = false;
    async function fetchPrinters() {
      try {
        const res = await apiFetch("/settings/printers");
        const data = await res.json();
        if (!cancelled) {
          setAvailablePrinters(res.ok && Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error("Failed to fetch printers for KDS screen:", err);
        if (!cancelled) setAvailablePrinters([]);
      }
    }
    fetchPrinters();
    return () => {
      cancelled = true;
    };
  }, [stationId]);
  const selectedPrinterMacs = useMemo(() => {
    const selectedIds = Array.isArray(settings?.selected_printer_ids) ? settings.selected_printer_ids : [];
    const selectedIdSet = new Set(selectedIds.map(value => Number(value)).filter(value => Number.isInteger(value) && value > 0));
    if (!selectedIdSet.size) return [];
    return availablePrinters.filter(printer => selectedIdSet.has(Number(printer?.id))).map(printer => String(printer?.macAddress || "").trim()).filter(Boolean);
  }, [availablePrinters, settings?.selected_printer_ids]);
  const prevTicketIdsRef = useRef(null);
  useEffect(() => {
    const currentIds = orders.map(o => o.ticket_instance_id);
    const prevIds = prevTicketIdsRef.current;
    if (prevIds == null) {
      prevTicketIdsRef.current = currentIds;
      return;
    }
    const newIds = currentIds.filter(id => !prevIds.includes(id));
    if (newIds.length > 0) {
      newIds.forEach(id => playNewTicketSoundOnce({
        ticket_instance_id: id
      }));
    }
    prevTicketIdsRef.current = currentIds;
  }, [orders, stationId]);
  const prevVoidedItemStateRef = useRef(null);
  useEffect(() => {
    const nextVoidedMap = new Map();
    for (const order of orders || []) {
      const ticketInstanceId = order?.ticket_instance_id ?? null;
      const orderId = order?.order_id ?? null;
      for (const item of order?.items || []) {
        const itemId = item?.ID ?? null;
        if (!itemId) continue;
        const stateKey = normalizeVoidedItemSoundKey({
          ticket_instance_id: ticketInstanceId,
          order_id: orderId,
          item_id: itemId
        });
        if (!stateKey) continue;
        const isVoided = item?.isVoided === true || Number(item?.TransType) === 2 || String(item?.status || "").toLowerCase() === "voided";
        nextVoidedMap.set(stateKey, {
          isVoided,
          payload: {
            ticket_instance_id: ticketInstanceId,
            order_id: orderId,
            item_id: itemId
          }
        });
      }
    }
    const previousVoidedMap = prevVoidedItemStateRef.current;
    if (previousVoidedMap == null) {
      prevVoidedItemStateRef.current = nextVoidedMap;
      return;
    }
    const newlyVoided = [];
    for (const [stateKey, nextEntry] of nextVoidedMap.entries()) {
      const previousEntry = previousVoidedMap.get(stateKey);
      if (nextEntry?.isVoided && !previousEntry?.isVoided) {
        newlyVoided.push(nextEntry.payload);
      }
    }
    if (newlyVoided.length > 0) {
      newlyVoided.forEach(payload => playVoidedItemSoundOnce(payload));
    }
    prevVoidedItemStateRef.current = nextVoidedMap;
  }, [orders, stationId]);
  useEffect(() => {
    const handleNewOrderSound = (payload = {}) => {
      const targetStationId = Number(payload?.station_id);
      const soundDecision = classifyIncomingTicketSound(payload);
      if (Number.isFinite(targetStationId) && Number(targetStationId) !== Number(stationId)) {
        return;
      }
      if (soundDecision.soundKey === "newTicket") {
        playNewTicketSoundOnce(payload);
        return;
      }
      if (soundDecision.soundKey === "addonReceived") {
        playEventSound("addonReceived");
        return;
      }
    };
    socket.on("new-order", handleNewOrderSound);
    return () => {
      socket.off("new-order", handleNewOrderSound);
    };
  }, [classifyIncomingTicketSound, stationId]);
  useEffect(() => {
    if (!soundSettingsLoaded) return;
    if (!audioUnlockedRef.current) return;
    const pendingEvent = pendingSoundEventRef.current;
    if (!pendingEvent) return;
    pendingSoundEventRef.current = null;
    window.setTimeout(() => {
      playEventSound(pendingEvent, {
        fromUnlock: true
      });
    }, 0);
  }, [soundSettingsLoaded, soundSettings, soundOptions]);
  useEffect(() => {
    const handleSettingsSyncRefresh = (payload = {}) => {
      const targetStationIds = Array.isArray(payload?.mirrored_station_ids) ? payload.mirrored_station_ids.map(value => Number(value)).filter(value => Number.isFinite(value)) : [];
      const currentStationId = Number(stationId);
      if (!Number.isFinite(currentStationId)) return;
      if (!targetStationIds.includes(currentStationId)) return;
      window.location.reload();
    };
    socket.on("settings-sync-refresh", handleSettingsSyncRefresh);
    return () => {
      socket.off("settings-sync-refresh", handleSettingsSyncRefresh);
    };
  }, [stationId]);
  const totalPages = Math.max(1, pages.length);
  useEffect(() => {
    const handleTouchStart = e => {
      if (isDragging || isPagePickerOpen) return;
      touchStartX.current = e.changedTouches[0].screenX;
    };
    const handleTouchEnd = e => {
      if (isDragging || isPagePickerOpen) return;
      touchEndX.current = e.changedTouches[0].screenX;
      const deltaX = touchEndX.current - touchStartX.current;
      if (deltaX > 50 && currentPage > 0) goToPage(currentPage - 1, {
        sync: true
      });else if (deltaX < -50 && currentPage < totalPages - 1) goToPage(currentPage + 1, {
        sync: true
      });
    };
    const main = document.getElementById("main-content");
    main?.addEventListener("touchstart", handleTouchStart);
    main?.addEventListener("touchend", handleTouchEnd);
    return () => {
      main?.removeEventListener("touchstart", handleTouchStart);
      main?.removeEventListener("touchend", handleTouchEnd);
    };
  }, [currentPage, totalPages, isDragging, isPagePickerOpen]);
  const removeTicketFromUI = ticket_instance_id => {
    setOrders(prev => prev.filter(o => o.ticket_instance_id !== ticket_instance_id));
    setPages(prev => {
      const prefix = `${ticket_instance_id}::`;
      const next = prev.map(pg => pg.filter(k => !k.startsWith(prefix)));
      const nonEmpty = next.length ? next : [[]];
      if (currentPage >= nonEmpty.length) {
        setCurrentPage(Math.max(0, nonEmpty.length - 1));
      }
      return nonEmpty;
    });
  };
  const bumpItemApi = async (ticket_instance_id, itemId) => {
    await apiFetch(`/orders/${ticket_instance_id}/bump`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        item_id: itemId,
        sender_station_id: stationId
      })
    });
  };
  const bumpTicketApi = async ticket_instance_id => {
    await apiFetch(`/orders/${ticket_instance_id}/bump`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        sender_station_id: stationId
      })
    });
  };
  const unbumpItemApi = async (ticket_instance_id, itemId) => {
    await apiFetch(`/orders/${ticket_instance_id}/unbump`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        item_id: itemId,
        sender_station_id: stationId,
        sender_role: role,
        sender_screen_id: screenMountId
      })
    });
  };
  const sendPrintJob = async (lines, printerMacs = selectedPrinterMacs) => {
    if (!Array.isArray(printerMacs) || !printerMacs.length) {
      throw new Error("No printers selected for this screen");
    }
    const jobs = printerMacs.map(async printerMac => {
      const res = await fetch(PRINT_SERVER_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          printerMac,
          lines,
          cut: true
        })
      });
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText || `Failed to send print job to ${printerMac}`);
      }
      return res.json().catch(() => null);
    });
    return Promise.all(jobs);
  };
  const sendPrintJobSafely = async (lines, contextLabel) => {
    try {
      await sendPrintJob(lines);
    } catch (err) {
      console.error(`[print-error] ${contextLabel}`, err);
    }
  };
  const buildPrintCourse = (order, courseIndex, settingsOverride = {}) => {
    const filteredItems = (order?.items || []).filter(item => {
      if (!item) return false;
      if (Number(item.course) !== Number(courseIndex)) return false;
      if (item.isMessage || item.isFireItem || item.isCourseSeparator) return false;
      if (item.isParked || String(item.status || "").toLowerCase() === "parked") return false;
      return true;
    });
    return buildPrintTicket({
      ...order,
      items: filteredItems
    }, {
      ...settingsOverride,
      coursing: false
    });
  };
  const isDisplayBumpableItem = (item = {}) => {
    const status = String(item?.status || "").toLowerCase();
    return !(item?.isCourseSeparator || item?.isMessage || item?.isFireItem || item?.isVoided || Number(item?.TransType) === 2 || status === "voided");
  };
  const recomputeCourseDisplayBoxes = order => {
    const currentCourseBoxes = order?.course_bump_display_boxes && typeof order.course_bump_display_boxes === "object" ? order.course_bump_display_boxes : {};
    const items = Array.isArray(order?.items) ? order.items : [];
    const nextCourseBoxes = {};
    for (const [courseKey, boxes] of Object.entries(currentCourseBoxes)) {
      const courseNum = Number(courseKey);
      const courseItems = items.filter(item => Number(item?.course) === courseNum && !item?.isCourseSeparator && !item?.isMessage);
      nextCourseBoxes[courseKey] = (Array.isArray(boxes) ? boxes : []).map(box => {
        const stationIdNum = Number(box?.station_id);
        const stationItems = courseItems.filter(item => {
          const itemBoxes = Array.isArray(item?.item_bump_display_boxes) ? item.item_bump_display_boxes : [];
          return itemBoxes.some(itemBox => Number(itemBox?.station_id) === stationIdNum && itemBox?.received);
        });
        const bumpableStationItems = stationItems.filter(isDisplayBumpableItem);
        const bumped = stationItems.length > 0 && bumpableStationItems.length > 0 && bumpableStationItems.every(item => (item.item_bump_display_boxes || []).some(itemBox => Number(itemBox?.station_id) === stationIdNum && itemBox?.received && itemBox?.bumped));
        return {
          ...box,
          received: stationItems.length > 0,
          bumped
        };
      });
    }
    return nextCourseBoxes;
  };
  const patchLocalDisplayBoxState = (order, itemIds, bumped) => {
    if (!order) return order;
    const targetIds = new Set((Array.isArray(itemIds) ? itemIds : [itemIds]).map(id => String(id)));
    if (!targetIds.size) return order;
    let changed = false;
    const nextItems = (order.items || []).map(item => {
      if (!targetIds.has(String(item?.ID))) return item;
      const boxes = Array.isArray(item?.item_bump_display_boxes) ? item.item_bump_display_boxes : [];
      if (!boxes.length) return item;
      const nextBoxes = boxes.map(box => {
        if (Number(box?.station_id) !== Number(stationId)) return box;
        changed = true;
        return {
          ...box,
          bumped: !!bumped,
          received: true
        };
      });
      return {
        ...item,
        item_bump_display_boxes: nextBoxes
      };
    });
    if (!changed) return order;
    const nextOrder = {
      ...order,
      items: nextItems
    };
    return {
      ...nextOrder,
      course_bump_display_boxes: recomputeCourseDisplayBoxes(nextOrder)
    };
  };
  const bumpAllRemainingItemsViaItemEndpoint = async order => {
    const ticketId = order.ticket_instance_id;
    const notBumped = (order.items || []).filter(i => !i.isCourseSeparator && !i.isMessage && !i.isBumped);
    if (notBumped.length > 0) {
      const nowIso = new Date().toISOString();
      setOrders(prev => prev.map(o => o.ticket_instance_id !== ticketId ? o : patchLocalDisplayBoxState({
        ...o,
        items: (o.items || []).map(it => notBumped.some(x => x.ID === it.ID) ? {
          ...it,
          isBumped: true,
          status: "bumped",
          bumpedAt: nowIso
        } : it)
      }, notBumped.map(x => x.ID), true)));
    }
    for (const item of notBumped) {
      await bumpItemApi(ticketId, item.ID);
      emitBumpItem(ticketId, item, true);
    }
    await bumpTicketApi(ticketId);
  };
  const handleBumpTicket = async order => {
    try {
      const shouldPrintTicketOnBump = settings?.enable_printing_from_ods && settings?.print_ticket_on_bump;
      const ticketPrintLines = shouldPrintTicketOnBump ? buildPrintTicket(order, {
        ...settings,
        hide_bumped_on_print: false
      }) : null;
      await bumpAllRemainingItemsViaItemEndpoint(order);
      if (ticketPrintLines) {
        await sendPrintJobSafely(ticketPrintLines, "ticket-on-bump");
      }
      playEventSound("ticketBumped");
      emitBumpTicket(order.ticket_instance_id);
      emitBumpAllCoursesForTicket(order.ticket_instance_id);
      removeTicketFromUI(order.ticket_instance_id);
    } catch (err) {
      console.error("Failed to bump ticket:", err);
    }
  };
  const handleBumpAllTickets = async () => {
    try {
      for (const order of orders) {
        await handleBumpTicket(order);
      }
    } catch (err) {
      console.error("Failed to bump all tickets:", err);
    }
  };
  const handleBumpItem = async (ticket_instance_id, item_id) => {
    const order = orders.find(o => o.ticket_instance_id === ticket_instance_id);
    const current = order?.items.find(i => i.ID === item_id);
    if (!current) return;
    if (current.isCourseSeparator || current.isMessage) return;
    const isVoided = current.isVoided || Number(current.TransType) === 2 || String(current.status || "").toLowerCase() === "voided";
    if (isVoided) return;
    if (current.isBumped) return;
    setOrders(prev => prev.map(o => o.ticket_instance_id !== ticket_instance_id ? o : patchLocalDisplayBoxState({
      ...o,
      items: (o.items || []).map(i => i.ID !== item_id ? i : {
        ...i,
        isBumped: true,
        status: "bumped",
        bumpedAt: new Date().toISOString()
      })
    }, item_id, true)));
    try {
      await bumpItemApi(ticket_instance_id, item_id);
      emitBumpItem(ticket_instance_id, current, true);
    } catch (err) {
      console.error("Failed to bump item:", err);
      setOrders(prev => prev.map(o => o.ticket_instance_id !== ticket_instance_id ? o : patchLocalDisplayBoxState({
        ...o,
        items: (o.items || []).map(i => i.ID !== item_id ? i : {
          ...i,
          isBumped: false,
          bumpedAt: null
        })
      }, item_id, false)));
    }
  };
  const handleToggleItemByDoubleTap = async (ticket_instance_id, item_id) => {
    const order = orders.find(o => o.ticket_instance_id === ticket_instance_id);
    const current = order?.items.find(i => i.ID === item_id);
    if (!current) return;
    if (current.isCourseSeparator || current.isMessage) return;
    const isVoided = current.isVoided || Number(current.TransType) === 2 || String(current.status || "").toLowerCase() === "voided";
    if (isVoided) return;
    if (!current.isBumped) {
      return handleBumpItem(ticket_instance_id, item_id);
    }
    setOrders(prev => prev.map(o => o.ticket_instance_id !== ticket_instance_id ? o : patchLocalDisplayBoxState({
      ...o,
      items: (o.items || []).map(i => i.ID !== item_id ? i : {
        ...i,
        isBumped: false,
        status: "pending",
        bumpedAt: null
      })
    }, item_id, false)));
    try {
      await unbumpItemApi(ticket_instance_id, item_id);
    } catch (err) {
      console.error("Failed to unbump item:", err);
      setOrders(prev => prev.map(o => o.ticket_instance_id !== ticket_instance_id ? o : patchLocalDisplayBoxState({
        ...o,
        items: (o.items || []).map(i => i.ID !== item_id ? i : {
          ...i,
          isBumped: true,
          status: "bumped"
        })
      }, item_id, true)));
    }
  };
  const handleBumpCourseFromCard = async (ticket_instance_id, courseNumber, courseItems = []) => {
    const bumpable = courseItems.filter(i => !i.isCourseSeparator && !i.isMessage && !i.isBumped);
    if (bumpable.length === 0) return;
    const selectedOrder = orders.find(o => o.ticket_instance_id === ticket_instance_id);
    const shouldPrintCourseOnBump = settings?.enable_printing_from_ods && settings?.print_course_on_bump;
    const coursePrintLines = shouldPrintCourseOnBump && selectedOrder ? buildPrintCourse(selectedOrder, courseNumber, {
      ...settings,
      hide_bumped_on_print: false
    }) : null;
    try {
      const nowIso = new Date().toISOString();
      setOrders(prev => prev.map(o => o.ticket_instance_id !== ticket_instance_id ? o : patchLocalDisplayBoxState({
        ...o,
        items: (o.items || []).map(it => bumpable.some(x => x.ID === it.ID) ? {
          ...it,
          isBumped: true,
          status: "bumped",
          bumpedAt: nowIso
        } : it)
      }, bumpable.map(x => x.ID), true)));
      for (const item of bumpable) {
        await bumpItemApi(ticket_instance_id, item.ID);
        emitBumpItem(ticket_instance_id, item, true);
      }
      if (coursePrintLines) {
        await sendPrintJobSafely(coursePrintLines, "course-on-bump");
      }
      emitBumpCourse(ticket_instance_id, Number(courseNumber));
      playEventSound("courseBumped");
    } catch (err) {
      console.error("Failed to bump course:", err);
    }
  };
  const handleCourseComplete = async ticket_instance_id => {
    try {
      const order = orders.find(o => o.ticket_instance_id === ticket_instance_id);
      const shouldPrintTicketOnBump = settings?.enable_printing_from_ods && settings?.print_ticket_on_bump;
      const ticketPrintLines = shouldPrintTicketOnBump && order ? buildPrintTicket(order, {
        ...settings,
        hide_bumped_on_print: false
      }) : null;
      if (order) await bumpAllRemainingItemsViaItemEndpoint(order);else await bumpTicketApi(ticket_instance_id);
      if (ticketPrintLines) {
        await sendPrintJobSafely(ticketPrintLines, "ticket-on-last-course-bump");
      }
      playEventSound("ticketBumped");
      emitBumpTicket(ticket_instance_id);
      removeTicketFromUI(ticket_instance_id);
    } catch (err) {
      console.error("Failed to finalize ticket on last course:", err);
    }
  };
  const handleSelectFireCourse = ({
    ticketId,
    orderId,
    course
  }) => {
    if (!isFireCourseMode) return;
    fireCourse(course, {
      ticket_instance_id: ticketId,
      order_id: orderId
    });
    playEventSound("courseFired");
    setIsPeekMode(false);
    setIsFireCourseMode(false);
  };
  const handleSelectFireItem = ({
    ticketId,
    orderId,
    itemId
  }) => {
    if (!isFireItemMode) return;
    fireItem(itemId, {
      ticket_instance_id: ticketId,
      order_id: orderId
    });
    playEventSound("courseFired");
    setIsPeekMode(false);
    setIsFireItemMode(false);
  };
  const handleFireTicketSelect = ticket => {
    if (!isFireTicketMode) return;
    setIsPeekMode(false);
    setIsFireTicketMode(false);
    setPeekModeIntent(null);
    if (settings.coursing) {
      const courses = new Set(ticket.items.filter(i => i.course != null && !i.isFired && !i.isBumped).map(i => Number(i.course)).filter(Number.isFinite));
      [...courses].sort((a, b) => a - b).forEach(course => {
        fireCourse(course, {
          ticket_instance_id: ticket.ticket_instance_id,
          order_id: ticket.order_id
        });
      });
      emitFireTicket({
        ticket_instance_id: ticket.ticket_instance_id,
        order_id: ticket.order_id
      });
    } else {
      FireAllItems({
        ticket_instance_id: ticket.ticket_instance_id,
        order_id: ticket.order_id
      });
    }
    playEventSound("courseFired");
  };
  const handleSelectPrintCourse = async ({
    ticketId,
    orderId,
    course
  }) => {
    if (!isPrintCourseMode) return null;
    const payload = {
      ticket_instance_id: ticketId,
      order_id: orderId ?? null,
      course
    };
    try {
      const selectedOrder = orders.find(o => o.ticket_instance_id === ticketId);
      if (!selectedOrder) {
        console.error("[print-error] course order not found");
        return null;
      }
      const lines = buildPrintCourse(selectedOrder, course, settings);
      await sendPrintJob(lines);
    } catch (err) {
      console.error("[print-error] course", err);
    } finally {
      setIsPeekMode(false);
      setIsPrintCourseMode(false);
    }
    return payload;
  };
  const handlePrintTicketSelect = async ticket => {
    if (!isPrintTicketMode) return null;
    const payload = {
      ticket_instance_id: ticket.ticket_instance_id,
      order_id: ticket.order_id ?? null,
      ticket
    };
    try {
      const lines = buildPrintTicket(ticket, settings);
      await sendPrintJob(lines);
    } catch (err) {
      console.error("[print-error] ticket", err);
    } finally {
      setIsPeekMode(false);
      setIsPrintTicketMode(false);
      setPeekModeIntent(null);
    }
    return payload;
  };
  const handleEnterParkingMode = () => {
    setIsPeekMode(true);
    setIsParkTicketMode(true);
    setPeekModeIntent("park");
  };
  const resetParkOptions = () => {
    setParkMode(PARK_MODE_MANUAL);
    setSpecificUnparkTime("");
    setDurationHours("0");
    setDurationMinutes("15");
    setParkSelectionTicket(null);
    setIsParkOptionsModalOpen(false);
  };
  const setDurationFromTotalMinutes = totalMinutes => {
    const bounded = Math.max(0, Math.min(23 * 60 + 59, totalMinutes));
    setDurationHours(String(Math.floor(bounded / 60)));
    setDurationMinutes(String(bounded % 60));
  };
  const adjustDurationByMinutes = deltaMinutes => {
    setParkMode(PARK_MODE_DURATION);
    const currentTotal = Math.max(0, Number(durationHours) || 0) * 60 + Math.max(0, Number(durationMinutes) || 0);
    setDurationFromTotalMinutes(currentTotal + deltaMinutes);
  };
  const applyDurationPreset = minutes => {
    setParkMode(PARK_MODE_DURATION);
    setDurationFromTotalMinutes(minutes);
  };
  const applySpecificTimePreset = minutesFromNow => {
    setParkMode(PARK_MODE_SPECIFIC);
    setSpecificUnparkTime(formatTimeInputInZone(new Date(Date.now() + minutesFromNow * 60 * 1000), stationTimezone));
  };
  const handleSelectParkTicket = async ticket => {
    if (!isParkTicketMode) return;
    setIsPeekMode(false);
    setIsParkTicketMode(false);
    setPeekModeIntent(null);
    setParkSelectionTicket(ticket);
    setIsParkOptionsModalOpen(true);
  };
  const handleConfirmParkTicket = async () => {
    if (!parkSelectionTicket?.ticket_instance_id) return;
    const scheduledUnparkIso = buildScheduledUnparkIso({
      parkMode,
      stationTimezone,
      specificTime: specificUnparkTime,
      durationHours,
      durationMinutes
    });
    if (parkMode !== PARK_MODE_MANUAL && !scheduledUnparkIso) return;
    parkTicket(parkSelectionTicket.ticket_instance_id, {
      unparkTime: scheduledUnparkIso
    });
    resetParkOptions();
  };
  const handleSelectUnparkTicket = async ticket => {
    unparkTicket(ticket.ticket_instance_id);
    setIsUnparkModalOpen(false);
    setSelectedParkedTicketId(null);
  };
  const handleOpenUnparkModal = () => {
    setIsPeekMode(false);
    setIsParkTicketMode(false);
    setPeekModeIntent(null);
    setSelectedParkedTicketId(parkedOrdersForMenu[0]?.ticket_instance_id ?? null);
    setIsUnparkModalOpen(true);
  };
  const displayName = stationName || (role === "expo" ? "Expo" : role ? role.charAt(0).toUpperCase() + role.slice(1) : `Station ${stationId}`);
  const scheduledUnparkPreview = buildScheduledUnparkIso({
    parkMode,
    stationTimezone,
    specificTime: specificUnparkTime,
    durationHours,
    durationMinutes
  });
  const parkedOrdersForMenu = useMemo(() => {
    const toTime = value => {
      const ms = Date.parse(value || "");
      return Number.isFinite(ms) ? ms : 0;
    };
    return [...orders].filter(o => o.isParked).sort((a, b) => {
      const parkDiff = toTime(b.parkedAt ?? b.parked_at) - toTime(a.parkedAt ?? a.parked_at);
      if (parkDiff !== 0) return parkDiff;
      return String(b.ticket_instance_id || "").localeCompare(String(a.ticket_instance_id || ""));
    });
  }, [orders]);
  const selectedParkedOrder = parkedOrdersForMenu.find(o => o.ticket_instance_id === selectedParkedTicketId) || parkedOrdersForMenu[0] || null;
  const scheduledUnparkLabel = parkMode === PARK_MODE_MANUAL ? "Manual unpark" : scheduledUnparkPreview ? formatScheduledPreview(scheduledUnparkPreview, stationTimezone) : parkMode === PARK_MODE_SPECIFIC ? "Select a local time" : "Enter a duration";
  return <div className="relative flex h-screen flex-col overflow-hidden font-sans bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-100">
      <TopBar stationId={stationId} displayName={displayName} onToggleDarkMode={toggleDarkMode} isDark={isDark} onOpenSettings={() => setShowSettings(true)} onBumpAll={handleBumpAllTickets} />

      {!isAudioUnlocked && <div className="absolute inset-x-0 top-20 z-40 flex justify-center px-4 pointer-events-none">
          <div className="pointer-events-auto rounded-2xl border border-amber-300 bg-amber-100/95 px-5 py-3 text-center text-sm font-semibold text-amber-950 shadow-lg dark:border-amber-200 dark:bg-amber-300/95 dark:text-slate-950">
            Tap anywhere to enable sound alerts
          </div>
        </div>}

      {totalPages > 1 && <>
          {currentPage > 0 && <button style={{
        zIndex: 30
      }} className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white dark:bg-gray-800 text-xl text-gray-800 dark:text-gray-100 shadow-lg rounded-full w-10 h-10 flex items-center justify-center z-30 hover:bg-gray-200 dark:hover:bg-gray-700" onClick={() => goToPage(currentPage - 1, {
        sync: true
      })} aria-label="Previous Page">
              ←
            </button>}
          {currentPage < totalPages - 1 && <button style={{
        zIndex: 30
      }} className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white dark:bg-gray-800 text-xl text-gray-800 dark:text-gray-100 shadow-lg rounded-full w-10 h-10 flex items-center justify-center z-30 hover:bg-gray-200 dark:hover:bg-gray-700" onClick={() => goToPage(currentPage + 1, {
        sync: true
      })} aria-label="Next Page">
              →
            </button>}
        </>}

      {peekedOrder && peekModeIntent === "view" && <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-70" onClick={() => {
      setPeekedOrder(null);
      setPeekModeIntent(null);
    }}>
          <div className="bg-white dark:bg-gray-800 p-4 rounded shadow max-w-xl w-full max-h-[90vh] overflow-y-auto relative z-[60] flex justify-center" onClick={e => e.stopPropagation()}>
            <TicketCard order={peekedOrder} orderIndex={0} totalCards={1} isPeekMode={isPeekMode} settings={{
          ...settings,
          hide_bumped_courses: false,
          hide_course: false
        }} onItemTap={() => {}} onItemDoubleTap={() => {}} onBump={() => {}} onBumpCourse={(ticketId, courseNumber, courseItems) => handleBumpCourseFromCard(ticketId, courseNumber, courseItems)} onCourseComplete={handleCourseComplete} isPrepScreen={false} isFireCourseMode={isFireCourseMode} onSelectFireCourse={handleSelectFireCourse} setIsFireCourseMode={setIsFireCourseMode} isFireItemMode={isFireItemMode} onSelectFireItem={handleSelectFireItem} />
          </div>
        </div>}

      {isPeekMode && <div className="fixed inset-0 z-40 bg-gray-400/50 dark:bg-gray-800/30 backdrop-blur-sm pointer-events-auto" />}

      {isPeekMode && <div className="fixed bottom-[5.5rem] left-1/2 transform -translate-x-1/2 z-[200] flex items-center gap-6">
          <button onClick={() => {
        setIsPeekMode(false);
        setIsFireTicketMode(false);
        setIsFireCourseMode(false);
        setIsFireItemMode(false);
        setIsPrintTicketMode(false);
        setIsPrintCourseMode(false);
        setIsParkTicketMode(false);
        setPeekModeIntent(null);
      }} className="px-4 py-2 rounded shadow border-2 border-yellow-400 bg-yellow-300 text-black shadow-yellow-300/30 font-semibold uppercase hover:bg-yellow-400">
            Cancel
          </button>
          {isParkTicketMode && <button onClick={handleOpenUnparkModal} className="px-5 py-2 rounded shadow border-2 border-cyan-500 bg-cyan-500 text-white dark:bg-cyan-400 dark:text-slate-950 dark:border-cyan-300 shadow-cyan-500/30 font-semibold uppercase hover:bg-cyan-600 dark:hover:bg-cyan-300">
              Unpark Ticket
            </button>}
        </div>}

      {(isFireCourseMode || isFireItemMode || isPrintCourseMode) && <div className="fixed inset-0 z-40 bg-gray-400/50 dark:bg-gray-800/30 backdrop-blur-sm pointer-events-auto" />}

      {(isFireCourseMode || isFireItemMode || isPrintCourseMode) && <button onClick={() => {
      setIsFireCourseMode(false);
      setIsFireItemMode(false);
      setIsPrintCourseMode(false);
    }} className="fixed bottom-[5.5rem] left-1/2 transform -translate-x-1/2 z-[200] px-4 py-2 rounded shadow border-2 border-yellow-400 bg-yellow-300 text-black shadow-yellow-300/30 font-semibold uppercase hover:bg-yellow-400">
          {isPrintCourseMode ? "Cancel Print Mode" : "Cancel Fire Mode"}
        </button>}

      <main id="main-content" className="min-h-0 flex-1 overflow-y-auto px-4 py-2 pb-4 bg-gray-100 dark:bg-gray-900">
        <ReactSortable onStart={() => setIsDragging(true)} onRemove={() => {
        skipNextGridSetList.current = true;
      }} onEnd={() => setIsDragging(false)} group={{
        name: "tickets",
        pull: "clone",
        put: true
      }} list={pageCards} setList={newList => {
        if (skipNextGridSetList.current) {
          skipNextGridSetList.current = false;
          return;
        }
        const currentPageKeys = pages[currentPage] ?? [];
        const nextPageKeys = newList.map(x => keyForOrder(x.order));
        const changedIndex = nextPageKeys.findIndex((key, index) => key !== currentPageKeys[index]);
        const changedKey = changedIndex >= 0 ? nextPageKeys[changedIndex] : null;
        const nextPages = pages.map((p, index) => index === currentPage ? nextPageKeys : [...p]);
        setPages(nextPages);
        if (changedKey) {
          const orderedKeys = nextPages.flat();
          emitTicketLocationSync({
            order: orderByKey[changedKey] || null,
            ticketKey: changedKey,
            orderedKeys,
            targetGlobalIndex: orderedKeys.indexOf(changedKey)
          });
        }
      }} animation={200} handle=".drag-handle" className={`relative pointer-events-auto flex flex-wrap items-start gap-4 justify-start ${isPeekMode || isFireCourseMode || isFireItemMode || isPrintCourseMode ? "z-50" : "z-20"}`} style={{
        width: touchScrollGutterPx > 0 ? `calc(100% - ${touchScrollGutterPx}px)` : "100%"
      }}>
          {pageCards.map(({
          key,
          order,
          orderIndex
        }) => <TicketCard key={key} order={order} orderIndex={orderIndex} totalPages={1} onItemTap={(ticketInstanceId, itemId) => handleBumpItem(ticketInstanceId, itemId)} onItemDoubleTap={(ticketInstanceId, itemId) => handleToggleItemByDoubleTap(ticketInstanceId, itemId)} onBump={handleBumpTicket} settings={settings} isPeekMode={isPeekMode} isFireCourseMode={isFireCourseMode} onSelectFireCourse={handleSelectFireCourse} setIsFireCourseMode={setIsFireCourseMode} isFireItemMode={isFireItemMode} onSelectFireItem={handleSelectFireItem} isPrintCourseMode={isPrintCourseMode} onSelectPrintCourse={handleSelectPrintCourse} isPrintTicketMode={isPrintTicketMode} setPeekedOrder={setPeekedOrder} setPeekModeIntent={setPeekModeIntent} onBumpCourse={(ticketId, courseNumber, courseItems) => handleBumpCourseFromCard(ticketId, courseNumber, courseItems)} onCourseComplete={handleCourseComplete} onPeekSelect={selectedOrder => {
          if (peekModeIntent === "view") {
            setPeekedOrder(selectedOrder);
            setIsPeekMode(false);
          } else if (peekModeIntent === "fire" && isFireTicketMode) {
            handleFireTicketSelect(selectedOrder);
            setPeekModeIntent(null);
          } else if (peekModeIntent === "print" && isPrintTicketMode) {
            handlePrintTicketSelect(selectedOrder);
          } else if (peekModeIntent === "park" && isParkTicketMode) {
            handleSelectParkTicket(selectedOrder);
            setPeekModeIntent(null);
          }
        }} />)}
        </ReactSortable>

        {isDragging && <div className="fixed w-full h-1/4 bottom-0 left-0 right-0 z-[70]">
            <div className="flex items-center justify-center py-2">
              <span className="text-gray-700 dark:text-gray-300 font-semibold bg-white/80 dark:bg-gray-900/70 px-3 py-1 rounded-full shadow">
                Drop to Choose Page
              </span>
            </div>

            <ReactSortable list={[]} setList={() => {}} group={{
          name: "tickets",
          pull: "clone",
          put: true
        }} sort={false} onAdd={evt => {
          if (evt.item && evt.from !== evt.to) {
            try {
              evt.to.removeChild(evt.item);
            } catch {}
          }
          const fromIndexOnPage = evt.oldIndex ?? 0;
          const k = pages[currentPage]?.[fromIndexOnPage];
          if (!k) return;
          setPendingDropKey(k);
          setIsPagePickerOpen(true);
        }} animation={150} id="bottom-drop-zone" className="flex items-center justify-center bg-gray-200/60 dark:bg-gray-700/60 border-t-2 border-dashed border-gray-400 dark:border-gray-500 w-full h-full" />
          </div>}

        {isPagePickerOpen && <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60" onClick={() => {
        setIsPagePickerOpen(false);
        setPendingDropKey(null);
      }}>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-xl max-w-2xl w-[90%]" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
                Send ticket to page…
              </h3>

              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {pages.map((_, idx) => <button key={idx} onClick={() => handlePickPage(idx)} className={`rounded-xl border px-4 py-6 text-center font-semibold shadow hover:shadow-md focus:outline-none focus:ring-2 ${idx === currentPage ? "bg-yellow-200 border-yellow-400 text-gray-900" : "bg-gray-50 dark:bg-gray-900 border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-100"}`}>
                    <div className="text-2xl">{idx + 1}</div>
                    <div className="text-xs opacity-70">Page</div>
                  </button>)}
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button onClick={() => {
              setIsPagePickerOpen(false);
              setPendingDropKey(null);
            }} className="px-4 py-2 rounded-md border bg-gray-100 dark:bg-gray-900 border-gray-300 dark:border-gray-700">
                  Cancel
                </button>
              </div>
            </div>
          </div>}
      </main>

      <div className="relative z-30 shrink-0">
        <FooterBar role={role} screenMountId={screenMountId} lastOrderId={orders.length > 0 ? orders[orders.length - 1].order_id ?? orders[orders.length - 1].ID ?? null : null} refreshOrders={setOrders} orders={orders} stationId={stationId} currentPage={currentPage} totalPages={totalPages} onFireTicket={() => {
        setIsPeekMode(true);
        setIsFireTicketMode(true);
        setIsFireCourseMode(false);
        setIsFireItemMode(false);
        setIsPrintTicketMode(false);
        setIsPrintCourseMode(false);
        setIsParkTicketMode(false);
        setPeekModeIntent("fire");
      }} onFireCourse={() => {
        setIsPeekMode(false);
        setIsFireTicketMode(false);
        setIsFireCourseMode(true);
        setIsFireItemMode(false);
        setIsPrintTicketMode(false);
        setIsPrintCourseMode(false);
        setIsParkTicketMode(false);
        setPeekModeIntent(null);
      }} onFireItem={() => {
        setIsPeekMode(false);
        setIsFireTicketMode(false);
        setIsFireCourseMode(false);
        setIsFireItemMode(true);
        setIsPrintTicketMode(false);
        setIsPrintCourseMode(false);
        setIsParkTicketMode(false);
        setPeekModeIntent(null);
      }} onPrintTicket={() => {
        setIsPeekMode(true);
        setIsFireTicketMode(false);
        setIsFireCourseMode(false);
        setIsFireItemMode(false);
        setIsPrintTicketMode(true);
        setIsPrintCourseMode(false);
        setIsParkTicketMode(false);
        setPeekModeIntent("print");
      }} onPrintCourse={() => {
        setIsPeekMode(false);
        setIsFireTicketMode(false);
        setIsFireCourseMode(false);
        setIsFireItemMode(false);
        setIsPrintTicketMode(false);
        setIsPrintCourseMode(true);
        setIsParkTicketMode(false);
        setPeekModeIntent(null);
      }} onViewFullTicket={() => {
        setIsPeekMode(true);
        setPeekModeIntent("view");
      }} settings={settings} onParkClick={handleEnterParkingMode} onTicketUnbumped={emitUnbumpTicket} refreshFromServer={fetchOrders} />
      </div>

      {showSettings && <SettingsModal stationId={stationId} onClose={() => setShowSettings(false)} />}

      {isParkOptionsModalOpen && <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/60" onClick={resetParkOptions}>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
              Park Ticket #{parkSelectionTicket?.order_id}
            </h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              Choose how this ticket should unpark. Specific times use {stationTimezone}.
            </p>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button onClick={() => setParkMode(PARK_MODE_MANUAL)} className={`rounded-2xl border px-4 py-4 text-left min-h-[96px] ${parkMode === PARK_MODE_MANUAL ? "border-amber-500 bg-amber-100 text-amber-950 dark:bg-amber-500/20 dark:text-amber-100" : "border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100"}`}>
                <div className="text-base font-semibold">Manual</div>
                <div className="mt-1 text-sm opacity-80">Unpark later by hand.</div>
              </button>
              <button onClick={() => setParkMode(PARK_MODE_SPECIFIC)} className={`rounded-2xl border px-4 py-4 text-left min-h-[96px] ${parkMode === PARK_MODE_SPECIFIC ? "border-amber-500 bg-amber-100 text-amber-950 dark:bg-amber-500/20 dark:text-amber-100" : "border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100"}`}>
                <div className="text-base font-semibold">Specific Time</div>
                <div className="mt-1 text-sm opacity-80">Pick a local clock time.</div>
              </button>
              <button onClick={() => setParkMode(PARK_MODE_DURATION)} className={`rounded-2xl border px-4 py-4 text-left min-h-[96px] ${parkMode === PARK_MODE_DURATION ? "border-amber-500 bg-amber-100 text-amber-950 dark:bg-amber-500/20 dark:text-amber-100" : "border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100"}`}>
                <div className="text-base font-semibold">After Duration</div>
                <div className="mt-1 text-sm opacity-80">Set hours and minutes.</div>
              </button>
            </div>

            <div className="mt-4 rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-4">
              {parkMode === PARK_MODE_MANUAL && <div className="min-h-[132px] flex flex-col justify-center">
                  <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">Manual unpark</div>
                  <div className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                    No timer will be set. The ticket stays parked until someone unparks it.
                  </div>
                </div>}

              {parkMode === PARK_MODE_SPECIFIC && <div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">Choose a local unpark time</div>
                  <div className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                    Uses {stationTimezone}. If the chosen time already passed today, it rolls to tomorrow.
                  </div>
                  <input type="time" value={specificUnparkTime} onChange={e => {
              setParkMode(PARK_MODE_SPECIFIC);
              setSpecificUnparkTime(e.target.value);
            }} className="mt-4 w-full rounded-2xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-5 text-2xl font-semibold text-center" />
                  <div className="mt-4 grid grid-cols-3 gap-3">
                    {specificTimePresetMinutes.map(minutes => <button key={minutes} onClick={() => applySpecificTimePreset(minutes)} className="rounded-xl bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 px-3 py-4 text-base font-semibold">
                        +{minutes}m
                      </button>)}
                  </div>
                </div>}

              {parkMode === PARK_MODE_DURATION && <div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">Set a countdown</div>
                  <div className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                    Use presets or adjust the timer in 15-minute steps.
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-3">
                    {durationPresetMinutes.map(minutes => <button key={minutes} onClick={() => applyDurationPreset(minutes)} className="rounded-xl bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 px-3 py-4 text-base font-semibold">
                        {minutes < 60 ? `${minutes}m` : `${Math.floor(minutes / 60)}h${minutes % 60 ? ` ${minutes % 60}m` : ""}`}
                      </button>)}
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-4">
                    <div className="rounded-2xl bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 p-3">
                      <div className="text-sm uppercase tracking-wide text-gray-500 dark:text-gray-400">Hours</div>
                      <input type="number" min="0" max="23" inputMode="numeric" value={durationHours} onChange={e => {
                  setParkMode(PARK_MODE_DURATION);
                  setDurationHours(e.target.value);
                }} className="mt-2 w-full bg-transparent text-center text-4xl font-bold outline-none" />
                    </div>
                    <div className="rounded-2xl bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 p-3">
                      <div className="text-sm uppercase tracking-wide text-gray-500 dark:text-gray-400">Minutes</div>
                      <input type="number" min="0" max="59" inputMode="numeric" value={durationMinutes} onChange={e => {
                  setParkMode(PARK_MODE_DURATION);
                  setDurationMinutes(e.target.value);
                }} className="mt-2 w-full bg-transparent text-center text-4xl font-bold outline-none" />
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-4 gap-3">
                    <button onClick={() => adjustDurationByMinutes(-15)} className="rounded-xl bg-gray-200 dark:bg-gray-700 px-3 py-4 text-base font-semibold">
                      -15m
                    </button>
                    <button onClick={() => adjustDurationByMinutes(-5)} className="rounded-xl bg-gray-200 dark:bg-gray-700 px-3 py-4 text-base font-semibold">
                      -5m
                    </button>
                    <button onClick={() => adjustDurationByMinutes(5)} className="rounded-xl bg-amber-100 dark:bg-amber-500/20 px-3 py-4 text-base font-semibold">
                      +5m
                    </button>
                    <button onClick={() => adjustDurationByMinutes(15)} className="rounded-xl bg-amber-100 dark:bg-amber-500/20 px-3 py-4 text-base font-semibold">
                      +15m
                    </button>
                  </div>
                </div>}
            </div>

            <div className="mt-4 rounded-lg bg-gray-100 dark:bg-gray-900 px-3 py-2 text-sm text-gray-700 dark:text-gray-200">
              Scheduled unpark: {scheduledUnparkLabel}
            </div>

            <div className="mt-5 flex justify-end gap-3">
              <button onClick={resetParkOptions} className="px-4 py-2 rounded-md border bg-gray-100 dark:bg-gray-900 border-gray-300 dark:border-gray-700">
                Cancel
              </button>
              <button onClick={handleConfirmParkTicket} disabled={parkMode !== PARK_MODE_MANUAL && !scheduledUnparkPreview} className="px-4 py-2 rounded-md bg-amber-500 text-black font-semibold disabled:opacity-50">
                Park Ticket
              </button>
            </div>
          </div>
        </div>}

      {isUnparkModalOpen && <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60" onClick={() => {
      setIsUnparkModalOpen(false);
      setSelectedParkedTicketId(null);
    }}>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-xl w-[95vw] max-w-7xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-bold mb-3 text-gray-900 dark:text-gray-100">
              📤 Unpark Ticket
            </h2>

            {parkedOrdersForMenu.length === 0 ? <div className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">
                No parked tickets.
              </div> : <div className="flex h-[72vh] gap-4">
                <div className="w-[320px] shrink-0 border-r border-gray-200 dark:border-gray-700 pr-3 overflow-y-auto">
                  <div className="space-y-2">
                    {parkedOrdersForMenu.map(ticket => {
                const isSelected = selectedParkedOrder?.ticket_instance_id === ticket.ticket_instance_id;
                return <button key={ticket.ticket_instance_id} type="button" onClick={() => setSelectedParkedTicketId(ticket.ticket_instance_id)} onTouchStart={e => {
                  e.preventDefault();
                  setSelectedParkedTicketId(ticket.ticket_instance_id);
                }} className={`w-full rounded-xl border px-3 py-3 text-left transition ${isSelected ? "border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-500/10" : "border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700/60"}`}>
                          <div className="text-sm font-semibold">
                            #{ticket.order_id} - {ticket.table_name || "No table"}
                          </div>
                          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            {ticket.server_name || "No server"}
                          </div>
                        </button>;
              })}
                  </div>
                </div>

                <div className="min-w-0 flex-1 overflow-y-auto">
                  {selectedParkedOrder ? <div className="flex flex-col items-center">
                      <div className="mb-3 flex w-full max-w-xl items-center justify-between gap-3">
                        <div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            Previewing parked ticket
                          </div>
                          <div className="text-base font-semibold">
                            #{selectedParkedOrder.order_id} - {selectedParkedOrder.table_name || "No table"}
                          </div>
                        </div>
                        <button onClick={() => handleSelectUnparkTicket(selectedParkedOrder)} onTouchStart={e => {
                  e.preventDefault();
                  handleSelectUnparkTicket(selectedParkedOrder);
                }} className="rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white hover:bg-blue-700 active:scale-[0.98] touch-manipulation">
                          Unpark
                        </button>
                      </div>

                      <div className="w-full flex justify-center">
                        <div className="max-w-xl">
                          <TicketCard order={selectedParkedOrder} orderIndex={0} totalCards={1} isPeekMode settings={settings} onItemTap={() => {}} onItemDoubleTap={() => {}} onBump={() => {}} onBumpCourse={() => {}} onCourseComplete={() => {}} isPrepScreen={false} isFireCourseMode={false} onSelectFireCourse={() => {}} setIsFireCourseMode={() => {}} isFireItemMode={false} onSelectFireItem={() => {}} isPrintCourseMode={false} onSelectPrintCourse={() => {}} isFireTicketMode={false} isPrintTicketMode={false} setPeekedOrder={() => {}} setPeekModeIntent={() => {}} />
                        </div>
                      </div>
                    </div> : null}
                </div>
              </div>}

            {false ? <ul className="max-h-64 overflow-y-auto space-y-2 text-sm">
              {orders.filter(o => o.isParked).length === 0 ? <li className="text-gray-500 dark:text-gray-400">No parked tickets.</li> : orders.filter(o => o.isParked).map(o => <li key={o.ticket_instance_id} className="flex justify-between items-center border-b border-gray-200 dark:border-gray-700 pb-1">
                      <span>
                        #{o.order_id} — {o.table_name || "No table"}
                      </span>
                      <button onClick={() => handleSelectUnparkTicket(o)} className="px-2 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-700">
                        Unpark
                      </button>
                    </li>)}
            </ul> : null}

            <div className="mt-3 text-right">
              <button onClick={() => setIsUnparkModalOpen(false)} className="px-3 py-1 text-sm rounded bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500">
                Close
              </button>
            </div>
          </div>
        </div>}
    </div>;
}